import { useState } from "react";
import { Button, message } from "antd";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { NoticeBanner, NOTICE_TONE } from "components/shared";
import LtrText from "components/shared/LtrText";
import { formatLocalizedDate } from "services/date";
import { trustThisBrowser } from "modules/auth/api";
import { DEVICE_ICON } from "modules/auth/components/profile/constants";
import { rememberTrustedBrowser } from "modules/auth/utils/trustedBrowsers";

/**
 * The step after a passed second factor: trust this browser, so signing in here skips the code.
 *
 * The user is already signed in at this point, so either answer — or a failure, since the
 * offer is single-use — ends in `onDone`.
 */
const TrustBrowserPrompt = ({ offer, userId, isDark, onDone }) => {
  const { t } = useTranslation();
  const [trusting, setTrusting] = useState(false);

  const trust = async () => {
    setTrusting(true);
    const response = await trustThisBrowser({ trust_ticket: offer.ticket });
    setTrusting(false);

    if (!response?.status) {
      message.error(
        response?.message || t("formValidationMessages.serverError")
      );
      onDone();
      return;
    }

    rememberTrustedBrowser(userId, {
      id: response.data?.trusted_browser?.id,
      token: response.data?.token,
    });
    onDone(response.message);
  };

  const mutedText = isDark ? "text-text-subtle" : "text-text-muted";

  return (
    <div className="mt-6">
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[5px] text-xs font-medium bg-pill-success-bg text-pill-success-text">
        <FontAwesomeIcon icon={faCircleCheck} />
        {t("profile.login.trustVerified")}
      </span>

      <h1
        className={`text-xl sm:text-2xl lg:text-[24px] font-bold mt-5 mb-2.5 leading-8 ${
          isDark ? "login-text-color" : "text-brand"
        }`}
      >
        {t("profile.login.trustTitle")}
      </h1>
      <p className={`text-sm leading-6 ${mutedText}`}>
        {t("profile.login.trustBody", {
          period: t("profile.login.trustDays", { count: offer.days }),
        })}
      </p>

      <div
        className={`mt-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 rounded-lg border p-4 ${
          isDark
            ? "border-chrome-raised bg-chrome-raised"
            : "border-border-light bg-surface"
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-11 h-11 shrink-0 rounded-xl bg-brand-tint text-brand flex items-center justify-center">
            <FontAwesomeIcon
              icon={DEVICE_ICON[offer.device_type] ?? DEVICE_ICON.desktop}
              className="text-[20px]"
            />
          </span>
          <div className="min-w-0">
            <div
              className={`text-sm font-bold ${isDark ? "login-text-color" : "text-ink"}`}
            >
              {offer.device_name}
            </div>
            {offer.ip_address && (
              <LtrText className={`block text-xs mt-0.5 ${mutedText}`}>
                {offer.ip_address}
              </LtrText>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className={`text-xs ${mutedText}`}>
            {t("profile.login.trustUntil")}
          </span>
          <span
            className={`text-[13px] font-bold ${isDark ? "login-text-color" : "text-ink"}`}
          >
            {formatLocalizedDate(offer.expires_at)}
          </span>
        </div>
      </div>

      <NoticeBanner tone={NOTICE_TONE.WARNING} className="mt-4">
        {t("profile.login.trustWarning")}
      </NoticeBanner>

      <div className="mt-7 flex flex-wrap gap-3">
        <Button
          type="primary"
          loading={trusting}
          onClick={trust}
          className="bg-brand hover:bg-brand-hover rounded-lg flex flex-1 basis-48 items-center justify-center h-[48px] sm:h-[56px] text-surface font-semibold text-sm sm:text-base"
        >
          {t("profile.login.trustConfirm")}
        </Button>
        <Button
          disabled={trusting}
          onClick={() => onDone()}
          className={`rounded-lg flex flex-1 basis-48 items-center justify-center h-[48px] sm:h-[56px] font-semibold text-sm sm:text-base border-control-border ${
            isDark ? "bg-transparent text-surface" : "bg-surface text-ink-body"
          }`}
        >
          {t("profile.login.trustSkip")}
        </Button>
      </div>

      <p className={`mt-5 text-xs text-center ${mutedText}`}>
        {t("profile.login.trustFootnote")}
      </p>
    </div>
  );
};

export default TrustBrowserPrompt;
